import { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BizButton } from '../../../Core/Components/Mini/BizButton';
import { useGetHolidays, useGetTimereports, useGetUserInfo } from '../../../Core/Hooks/useTimereport';
import { Holiday } from '../../../Core/Types/admin/timereport';
import { formatLocalDate } from '../../../Core/Utils/dateUtils';
import './TimereportCalendar.scss';
import { getISOWeekNumber, getMonthDays } from '../../../Core/Utils/calendarUtils';

type TimereportCalendarProps = {
	showTimereports?: boolean;
	minimal?: boolean;
	onSelectDate?: (dateStr: string) => void;
};

const TimereportCalendar = ({ showTimereports = true, minimal = false, onSelectDate }: TimereportCalendarProps) => {
	const { t, i18n } = useTranslation();
	const [year, setYear] = useState(() => new Date().getFullYear());
	const [month, setMonth] = useState(() => new Date().getMonth());
	const dayHeaders = [
		t('TIMEREPORT_USER.REPORT_VIEW.SHARED.WEEKDAYS.MON'),
		t('TIMEREPORT_USER.REPORT_VIEW.SHARED.WEEKDAYS.TUE'),
		t('TIMEREPORT_USER.REPORT_VIEW.SHARED.WEEKDAYS.WED'),
		t('TIMEREPORT_USER.REPORT_VIEW.SHARED.WEEKDAYS.THU'),
		t('TIMEREPORT_USER.REPORT_VIEW.SHARED.WEEKDAYS.FRI'),
		t('TIMEREPORT_USER.REPORT_VIEW.SHARED.WEEKDAYS.SAT'),
		t('TIMEREPORT_USER.REPORT_VIEW.SHARED.WEEKDAYS.SUN'),
	];
	const locale = i18n.resolvedLanguage?.startsWith('sv') ? 'sv-SE' : 'en-GB';

	const monthStart = useMemo(() => new Date(year, month, 1), [year, month]);
	const monthEnd = useMemo(() => new Date(year, month + 1, 0), [year, month]);

	const { timereports } = useGetTimereports(
		showTimereports ? formatLocalDate(monthStart) : '',
		showTimereports ? formatLocalDate(monthEnd) : ''
	);
	const { holidays } = useGetHolidays();
	const { userInfo } = useGetUserInfo();

	const flagDateStr = userInfo?.dateTimeReportFlag?.split('T')[0] ?? null;
	const employmentDateStr = userInfo?.dateEmployment?.split('T')[0] ?? null;

	const holidaysByDate = useMemo(() => {
		const map = new Map<string, Holiday>();
		if (!holidays) return map;
		for (const h of holidays) {
			const dateKey = h.date.split('T')[0];
			map.set(dateKey, h);
		}
		return map;
	}, [holidays]);

	const reportsByDate = useMemo(() => {
		const map = new Map<string, { hours: number; count: number; allSubmitted: boolean; allAttested: boolean }>();
		if (!timereports) return map;
		for (const item of timereports) {
			const dateKey = item.date.split('T')[0];
			const existing = map.get(dateKey) ?? { hours: 0, count: 0, allSubmitted: true, allAttested: true };
			existing.hours += item.units;
			existing.count += 1;
			if (!item.isSubmitted) existing.allSubmitted = false;
			if (!item.isAttested) existing.allAttested = false;
			map.set(dateKey, existing);
		}
		return map;
	}, [timereports]);

	const calendarDays = useMemo(() => getMonthDays(year, month), [year, month]);

	const monthTotal = useMemo(() => {
		let total = 0;
		reportsByDate.forEach((val) => {
			total += val.hours;
		});
		return total;
	}, [reportsByDate]);

	const handlePrevMonth = () => {
		if (month === 0) {
			setYear((y) => y - 1);
			setMonth(11);
		} else {
			setMonth((m) => m - 1);
		}
	};

	const handleNextMonth = () => {
		if (month === 11) {
			setYear((y) => y + 1);
			setMonth(0);
		} else {
			setMonth((m) => m + 1);
		}
	};

	// hidden for now because it confuses users. Can be added back later if needed.
	// const handleThisMonth = () => {
	// 	setYear(new Date().getFullYear());
	// 	setMonth(new Date().getMonth());
	// };

	const todayStr = formatLocalDate(new Date());
	const monthLabel = monthStart.toLocaleDateString(locale, { year: 'numeric', month: 'long' });

	return (
		<div className={`biz-timereport-calendar${minimal ? ' biz-timereport-calendar--minimal' : ''}`}>
			<div className='biz-timereport-calendar__navigation'>
				<BizButton text='◀' onClick={handlePrevMonth} />
				{/* <BizButton text={t('TIMEREPORT_USER.REPORT_VIEW.CALENDAR.ACTIONS.THIS_MONTH')} onClick={handleThisMonth} /> */}
				<span className='biz-timereport-calendar__month-label'>{monthLabel}</span>
				<BizButton text='▶' onClick={handleNextMonth} />
			</div>

			<div className='biz-timereport-calendar__grid'>
				<div className='biz-timereport-calendar__week-header'>
					{t('TIMEREPORT_USER.REPORT_VIEW.CALENDAR.WEEK_HEADER')}
				</div>
				{dayHeaders.map((header) => (
					<div key={header} className='biz-timereport-calendar__day-header'>
						{header}
					</div>
				))}

				{calendarDays.map((day, index) => {
					const data = reportsByDate.get(day.dateStr);
					const holiday = holidaysByDate.get(day.dateStr);
					const dayOfWeek = day.date.getDay();
					const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
					const isToday = day.dateStr === todayStr;
					const isFlagDate = flagDateStr === day.dateStr;
					const isBeforeEmployment = employmentDateStr != null && day.dateStr < employmentDateStr;
					const isLocked = isBeforeEmployment || (flagDateStr != null && day.dateStr <= flagDateStr);

					let statusClass = '';
					if (data?.allAttested && data.count > 0) statusClass = ' biz-timereport-calendar__day--attested';
					else if (data?.allSubmitted && data.count > 0) statusClass = ' biz-timereport-calendar__day--submitted';
					else if (data && data.count > 0) statusClass = ' biz-timereport-calendar__day--has-entries';

					let holidayClass = '';
					if (holiday) {
						holidayClass =
							holiday.hours >= 8
								? ' biz-timereport-calendar__day--holiday-8'
								: ' biz-timereport-calendar__day--holiday-4';
					}

					return (
						<Fragment key={day.dateStr}>
							{index % 7 === 0 && (
								<div className='biz-timereport-calendar__week-number'>{getISOWeekNumber(day.date)}</div>
							)}
							<div
								className={`biz-timereport-calendar__day${!day.isCurrentMonth ? ' biz-timereport-calendar__day--outside' : ''}${isWeekend ? ' biz-timereport-calendar__day--weekend' : ''}${isToday ? ' biz-timereport-calendar__day--today' : ''}${isFlagDate ? ' biz-timereport-calendar__day--flag' : ''}${isLocked ? ' biz-timereport-calendar__day--locked' : ''}${holidayClass}${statusClass}`}
								onClick={onSelectDate ? () => onSelectDate(day.dateStr) : undefined}
							>
								<div className='biz-timereport-calendar__day-number'>{day.date.getDate()}</div>
								{holiday && (
									<div className='biz-timereport-calendar__day-holiday-label'>
										{!minimal &&
											(holiday.hours >= 8
												? t('TIMEREPORT_USER.REPORT_VIEW.CALENDAR.LABELS.HOLIDAY')
												: t('TIMEREPORT_USER.REPORT_VIEW.CALENDAR.LABELS.HOLIDAY_HOURS', {
														hours: holiday.hours,
													}))}
									</div>
								)}
								{showTimereports && data && data.count > 0 && (
									<>
										<div className='biz-timereport-calendar__day-hours'>{data.hours}h</div>
										<div className='biz-timereport-calendar__day-count'>
											{t(
												data.count === 1
													? 'TIMEREPORT_USER.REPORT_VIEW.CALENDAR.LABELS.ENTRY_COUNT_ONE'
													: 'TIMEREPORT_USER.REPORT_VIEW.CALENDAR.LABELS.ENTRY_COUNT_OTHER',
												{ count: data.count }
											)}
										</div>
									</>
								)}
							</div>
						</Fragment>
					);
				})}
			</div>

			{showTimereports && (
				<div className='biz-timereport-calendar__month-total'>
					{t('TIMEREPORT_USER.REPORT_VIEW.CALENDAR.LABELS.MONTH_TOTAL', { hours: monthTotal })}
				</div>
			)}

			{flagDateStr && (
				<div className='biz-timereport-calendar__flag-info'>
					<span className='biz-timereport-calendar__flag-indicator' />
					{t('TIMEREPORT_USER.REPORT_VIEW.CALENDAR.LABELS.LAST_SUBMITTED', { date: flagDateStr })}
				</div>
			)}
		</div>
	);
};

export default TimereportCalendar;
